const SEARCH_PAGE_RECENT_SEARCHES_KEY = "unbound-recent-searches";
const SEARCH_PAGE_RECENT_SEARCHES_LIMIT = 6;

function normalizeSearchPageTerm(value) {
  return (value || "").trim().replace(/\s+/g, " ");
}

function getSearchPageRecentSearches() {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(SEARCH_PAGE_RECENT_SEARCHES_KEY) || "[]",
    );
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveSearchPageRecentSearch(term) {
  const normalized = normalizeSearchPageTerm(term);
  if (!normalized) return;

  const recent = getSearchPageRecentSearches().filter(
    (item) => item.toLowerCase() !== normalized.toLowerCase(),
  );

  recent.unshift(normalized);
  window.localStorage.setItem(
    SEARCH_PAGE_RECENT_SEARCHES_KEY,
    JSON.stringify(recent.slice(0, SEARCH_PAGE_RECENT_SEARCHES_LIMIT)),
  );
}

function parseExcludedCollectionHandles(rawValue) {
  return String(rawValue || "")
    .split(/[\r\n|]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function escapeSearchPageHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };

    return entities[character];
  });
}

function normalizeSearchResultProductId(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  if (normalized.startsWith("gid://")) return normalized;

  const numericMatch = normalized.match(/\d+/)?.[0];
  return numericMatch ? `gid://shopify/Product/${numericMatch}` : normalized;
}

function buildResultsHeadingText(query, totalCount) {
  if (!query) return "Search Results";

  const normalizedTotal = Number.parseInt(totalCount, 10) || 0;
  if (normalizedTotal <= 0) {
    return `0 results found for “${query}”`;
  }

  return `${normalizedTotal} ${normalizedTotal === 1 ? "result" : "results"} found for “${query}”`;
}

document.addEventListener("DOMContentLoaded", async () => {
  const searchBanner = document.querySelector("[data-search-page-term]");
  const form = document.querySelector(".search-banner__form");
  const input = document.querySelector(".search-banner__input");
  const searchIcon = document.querySelector(".search-banner__icon-search");
  const clearIcon = document.querySelector(".search-banner__icon-clear");
  const collectionsContainer = document.querySelector("[data-search-page-collections]");
  const resultsContainer = document.querySelector("#product-grid");
  const mainSearch = document.querySelector(".main-search");
  const resultsHeading = document.querySelector(".search-banner__results");
  const desktopResultsCount = document.querySelector("#ProductCountDesktop");
  const zeroResultsMessage = searchBanner?.dataset.zeroResultsMessage || "";
  const searchApi = window.UnboundSearchApi || null;
  const searchTracker = window.UnboundSearchTracking || null;

  if (!searchBanner || !form || !input) return;

  const currentTerm = normalizeSearchPageTerm(searchBanner.dataset.searchPageTerm);
  const searchPerformed = searchBanner.dataset.searchPerformed === "true";
  const resultsCount = Number.parseInt(searchBanner.dataset.searchResultsCount || "0", 10);
  const currentPage = Number.parseInt(resultsContainer?.dataset.searchResultsPage || "1", 10) || 1;
  const pageSize =
    Number.parseInt(resultsContainer?.dataset.searchResultsPageSize || "24", 10) || 24;
  const searchState = {
    clicked: false,
    exited: false,
  };
  let resolvedResultCount = resultsCount;
  let resolvedZeroResults = searchPerformed && resultsCount === 0;
  let hasTrackedInitialResults = false;
  const isSearchShadeLoading = () =>
    searchBanner.dataset.searchShadeLoading === "true" ||
    mainSearch?.dataset.searchShadeLoading === "true";

  const updateHeading = (totalCount) => {
    if (!resultsHeading) return;
    resultsHeading.textContent = buildResultsHeadingText(currentTerm, totalCount);
  };

  const updateDesktopCount = (totalCount) => {
    if (!desktopResultsCount || !currentTerm) return;
    desktopResultsCount.textContent = buildResultsHeadingText(currentTerm, totalCount);
  };

  const updateResolvedResultsCount = (totalCount) => {
    searchBanner.dataset.searchResultsResolvedCount = String(
      Number.parseInt(totalCount, 10) || 0,
    );
  };

  const refreshSearchProductCountDisplay = () => {
    if (!resultsContainer || !searchPerformed || isSearchShadeLoading()) return;

    const cards = Array.from(resultsContainer.querySelectorAll(".product-card"));
    const visibleCards = cards.filter(
      (card) => card.dataset.searchShadeExcluded !== "true" && card.style.display !== "none"
    );
    const visibleCount = visibleCards.length;

    resolvedResultCount = visibleCount;
    resolvedZeroResults = visibleCount === 0;
    updateResolvedResultsCount(visibleCount);
    updateHeading(visibleCount);
    updateDesktopCount(visibleCount);
    setWarningVisibility(visibleCount === 0);

    if (visibleCount === 0) {
      searchBanner.style.margin = "0";
      mainSearch?.classList.add("hidden");
      return;
    }

    mainSearch?.classList.remove("hidden");
  };

  window.refreshSearchProductCountDisplay = refreshSearchProductCountDisplay;

  const setWarningVisibility = (visible) => {
    let warningElement = searchBanner.querySelector(".search-banner__warning");

    if (visible && !warningElement && zeroResultsMessage) {
      warningElement = document.createElement("p");
      warningElement.className = "search-banner__warning";
      warningElement.textContent = zeroResultsMessage;
      searchBanner.querySelector(".search-banner__content")?.appendChild(warningElement);
    }

    if (!warningElement) return;
    warningElement.hidden = !visible;
  };

  const trackInitialResultsState = () => {
    if (
      hasTrackedInitialResults ||
      !searchTracker?.isEnabled?.() ||
      !currentTerm ||
      !searchPerformed
    ) {
      return;
    }

    hasTrackedInitialResults = true;

    if (resolvedZeroResults) {
      searchTracker.trackZeroResults({
        query: currentTerm,
        surface: "RESULTS",
        resultCount: 0,
      });
      return;
    }

    if (resolvedResultCount > 0) {
      searchTracker.trackResultsImpression({
        query: currentTerm,
        resultCount: resolvedResultCount,
      });
    }
  };

  const trackSearchExit = () => {
    if (
      searchState.clicked ||
      searchState.exited ||
      !searchTracker?.isEnabled?.() ||
      !currentTerm ||
      !searchPerformed
    ) {
      return;
    }

    searchState.exited = true;
    searchTracker.trackExit(
      {
        query: currentTerm,
        surface: "RESULTS",
        resultCount: resolvedResultCount,
      },
      { transport: "beacon" },
    );
  };

  const applyZeroResultsState = () => {
    searchBanner.removeAttribute("data-search-shade-loading");
    mainSearch?.removeAttribute("data-search-shade-loading");
    resolvedResultCount = 0;
    resolvedZeroResults = true;
    searchBanner.style.margin = "0";
    updateResolvedResultsCount(0);
    updateHeading(0);
    updateDesktopCount(0);
    setWarningVisibility(true);
    mainSearch?.classList.add("hidden");
  };

  const applyAppResults = (response) => {
    const appProducts = Array.isArray(response?.products) ? response.products : [];

    if (response?.zeroResults || !appProducts.length) {
      applyZeroResultsState();
      return true;
    }

    if (!resultsContainer || !resultsContainer.querySelector(".product-card")) {
      return false;
    }

    const existingCards = Array.from(resultsContainer.querySelectorAll(".product-card"));
    const cardLookup = new Map();

    existingCards.forEach((card) => {
      const rawId = String(card.dataset.productId || "").trim();
      const gid = normalizeSearchResultProductId(rawId);
      const numericId = rawId.match(/\d+/)?.[0] || "";
      const handle = normalizeSearchPageTerm(card.dataset.productHandle).toLowerCase();

      [gid, numericId, handle].filter(Boolean).forEach((key) => {
        if (!cardLookup.has(key)) {
          cardLookup.set(key, card);
        }
      });
    });

    const orderedCards = appProducts
      .map((product) => {
        const productId = normalizeSearchResultProductId(product.productId || product.id);
        const numericId = productId.match(/\d+/)?.[0] || "";
        const handle = normalizeSearchPageTerm(product.handle).toLowerCase();

        return cardLookup.get(productId) || cardLookup.get(numericId) || cardLookup.get(handle) || null;
      })
      .filter(Boolean);

    if (!orderedCards.length) {
      return false;
    }

    const fragment = document.createDocumentFragment();
    orderedCards.forEach((card) => fragment.appendChild(card));
    resultsContainer.replaceChildren(fragment);

    const resolvedAppResultCount =
      orderedCards.length === appProducts.length
        ? Number.parseInt(response?.pagination?.total, 10) || appProducts.length
        : orderedCards.length;

    resolvedResultCount = resolvedAppResultCount;
    resolvedZeroResults = false;
    setWarningVisibility(false);
    mainSearch?.classList.remove("hidden");

    if (isSearchShadeLoading()) {
      return true;
    }

    updateResolvedResultsCount(resolvedAppResultCount);
    updateHeading(resolvedAppResultCount);
    updateDesktopCount(resolvedAppResultCount);
    return true;
  };

  if (currentTerm) {
    saveSearchPageRecentSearch(currentTerm);
  }

  updateResolvedResultsCount(resolvedResultCount);
  refreshSearchProductCountDisplay();

  if (searchApi?.isConfigured?.() && currentTerm && searchPerformed) {
    try {
      const appResults = await searchApi.fetchResults(currentTerm, {
        page: currentPage,
        pageSize,
        context: {
          marketId: "us",
        },
      });
      const redirectUrl = searchApi.getRedirectUrl(appResults?.redirect);

      if (
        redirectUrl &&
        redirectUrl !== `${window.location.pathname}${window.location.search}` &&
        redirectUrl !== window.location.href
      ) {
        window.location.assign(redirectUrl);
        return;
      }

      applyAppResults(appResults);
      window.syncSearchShadeProductCards?.(document);
      refreshSearchProductCountDisplay();
    } catch {
      // Keep current Shopify-rendered results as fallback.
    }
  }

  trackInitialResultsState();

  if (resultsContainer && currentTerm && searchPerformed) {
    resultsContainer.addEventListener("click", (event) => {
      const resultLink = event.target.closest(".product-card__link");
      if (!resultLink) return;

      const productCard = resultLink.closest(".product-card");
      if (!productCard) return;

      const productCards = Array.from(resultsContainer.querySelectorAll(".product-card"));
      const position = productCards.indexOf(productCard) + 1;

      searchState.clicked = true;

      if (!searchTracker?.isEnabled?.()) return;

      searchTracker.trackResultClick(
        {
          query: currentTerm,
          productId: productCard.dataset.productId || "",
          position: position > 0 ? position : null,
          resultCount: resolvedResultCount,
        },
        { transport: "beacon" },
      );
    });
  }

  searchIcon?.addEventListener("click", () => {
    if (normalizeSearchPageTerm(input.value)) {
      form.requestSubmit();
    }
  });

  clearIcon?.addEventListener("click", () => {
    input.value = "";
    input.focus();
  });

  form.addEventListener("submit", (event) => {
    const query = normalizeSearchPageTerm(input.value);

    if (!query) {
      event.preventDefault();
      input.focus();
      return;
    }

    saveSearchPageRecentSearch(query);
    searchTracker?.trackSearchSubmitted?.(
      {
        query,
        surface: "RESULTS",
      },
      { transport: "beacon" },
    );
  });

  window.addEventListener("pagehide", trackSearchExit);

  if (!collectionsContainer || !currentTerm) return;

  const predictiveSearchUrl = `${window.routes?.predictive_search_url || "/search/suggest"}.json`;
  const url = new URL(predictiveSearchUrl, window.location.origin);
  url.searchParams.set("q", currentTerm);
  url.searchParams.set("resources[type]", "collection");
  url.searchParams.set("resources[limit]", "8");

  try {
    const response = await fetch(url.toString());
    const data = await response.json();
    const collections = Array.isArray(data?.resources?.results?.collections)
      ? data.resources.results.collections
      : [];
    const excludedHandles = parseExcludedCollectionHandles(
      collectionsContainer.dataset.excludedCollectionHandles,
    );

    const filteredCollections = collections.filter((collection) => {
      const handle = String(collection.handle || "").toLowerCase();
      const title = String(collection.title || "").toLowerCase();
      const urlValue = String(collection.url || "").toLowerCase();

      if (!handle || excludedHandles.includes(handle)) return false;
      if (handle.includes("affiliate") || title.includes("affiliate") || urlValue.includes("affiliate")) {
        return false;
      }

      return true;
    });

    if (!filteredCollections.length) return;

    collectionsContainer.innerHTML = filteredCollections
      .slice(0, 6)
      .map((collection) => {
        const href = collection.url || `${collectionsContainer.dataset.searchPageUrl}?q=${encodeURIComponent(collection.title || "")}`;
        const title = escapeSearchPageHtml(collection.title || "");

        return `
          <a class="search-banner__collection-pill" href="${href}">
            ${title}
          </a>
        `;
      })
      .join("");

    collectionsContainer.classList.remove("hidden");
  } catch {
    collectionsContainer.classList.add("hidden");
  }
});
